import React, { useState, useRef } from 'react';
import {
  Modal,
  PrimaryButton,
  DefaultButton,
  TextField,
  MessageBar,
  MessageBarType,
  Stack,
  Text,
  Checkbox,
} from '@fluentui/react';
import { useMutation, useQuery } from '@apollo/client';
import { SIGN_UP, SIGN_IN, SEND_CONFIRMATION_CODE, VERIFY_CONFIRMATION_CODE, RESET_PASSWORD } from '../../graphql/mutations';
import { CHECK_EMAIL_EXISTS } from '../../graphql/queries';

type AuthMode = 'signup' | 'signin' | 'confirm';

interface FormData {
  username: string;
  email: string;
  phone: string;
  password: string;
  company?: string;
}

export const AuthModal: React.FC<{
  isOpen: boolean;
  onDismiss: () => void;
}> = ({ isOpen, onDismiss }) => {
  const [mode, setMode] = useState<AuthMode>('signin');
  const [formData, setFormData] = useState<FormData>({
    username: '',
    email: '',
    phone: '',
    password: '',
    company: ''
  });
  const [repeatPassword, setRepeatPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; type: MessageBarType } | null>(null);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const debouncedCheckEmailRef = useRef<NodeJS.Timeout | null>(null);

  // Сохраняем нормализованный телефон после валидации
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null);
  const [confirmedPhone, setConfirmedPhone] = useState<string | null>(null);

  // ✅ Состояние согласия с политикой конфиденциальности
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const [signUp] = useMutation(SIGN_UP);
  const [signIn] = useMutation(SIGN_IN);
  const [sendConfirmationCode] = useMutation(SEND_CONFIRMATION_CODE);
  const [verifyConfirmationCode] = useMutation(VERIFY_CONFIRMATION_CODE);
  const [resetPassword] = useMutation(RESET_PASSWORD);
  const { refetch } = useQuery(CHECK_EMAIL_EXISTS, { skip: true });

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[\w\u0400-\u04FF.-]+@[\w\u0400-\u04FF.-]+\.(ru|рф)$/i;
    return emailRegex.test(email);
  };

  const handleChange = (
    e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    const target = e.target as HTMLInputElement;
    const name = target.name;
    if (!name) return;

    setFormData(prev => ({ ...prev, [name]: newValue || '' }));
    setSubmitError(null);
    setMessage(null);

    if (mode === 'signup' && name === 'email' && newValue && newValue.includes('@')) {
      if (debouncedCheckEmailRef.current) {
        clearTimeout(debouncedCheckEmailRef.current);
      }
      debouncedCheckEmailRef.current = setTimeout(() => {
        if (!isValidEmail(newValue)) {
          setEmailError('Пожалуйста, введите корректный email в российской зоне (.ru или .рф)');
        } else {
          setEmailError(null);
          checkEmail(newValue);
        }
      }, 500);
    }
  };

  const checkEmail = async (email: string) => {
    try {
      const { data } = await refetch({ email });
      if (mode === 'signup' && data?.checkEmailExists) {
        setSubmitError('Пользователь с этим email уже зарегистрирован');
      }
      if (mode === 'signin' && !data?.checkEmailExists) {
        setSubmitError('Пользователь с этим email не найден');
      }
    } catch (err) {
      console.error('Ошибка проверки email:', err);
    }
  };

  // 🔽 ЕДИНАЯ ФУНКЦИЯ НОРМАЛИЗАЦИИ ТЕЛЕФОНА (как в первом фронтенде)
  const normalizePhone = (input: string): { isValid: boolean; normalized?: string; error?: string } => {
    const digits = input.replace(/\D/g, '');

    if (!digits) return { isValid: false, error: 'Введите номер телефона' };

    let cleaned: string;

    if (digits.startsWith('8') && digits.length === 11) {
      cleaned = digits.slice(1); // убираем 8 → 10 цифр
    } else if (digits.startsWith('7') && digits.length === 11) {
      cleaned = digits.slice(1); // убираем 7 → 10 цифр
    } else if (digits.length === 10) {
      cleaned = digits; // уже 10 цифр — считаем, что +7
    } else {
      return { isValid: false, error: 'Введите корректный номер (10 цифр)' };
    }

    if (cleaned.length !== 10) {
      return { isValid: false, error: 'Номер должен содержать 10 цифр после +7' };
    }

    return { isValid: true, normalized: '+7' + cleaned };
  };

  const validateForm = (): boolean => {
    if (mode === 'signup' && formData.password !== repeatPassword) {
      setSubmitError('Пароли не совпадают');
      return false;
    }

    if (mode === 'signup' && !formData.username.trim()) {
      setSubmitError('Введите имя пользователя');
      return false;
    }

    if (!formData.password.trim()) {
      setSubmitError('Введите пароль');
      return false;
    }

    if (!isValidEmail(formData.email)) {
      setEmailError('Введите корректный email');
      return false;
    }

    if (mode === 'signup' && !formData.phone.trim()) {
      setSubmitError('Введите номер телефона');
      return false;
    }

    // ✅ Проверка телефона
    const { isValid, error } = normalizePhone(formData.phone);
    if (!isValid) {
      setSubmitError(error || 'Некорректный номер телефона');
      return false;
    }

    setEmailError(null);
    return true;
  };

  const handleSendConfirmationCode = async () => {
    if (!formData.email.trim()) {
      return setSubmitError('Введите email');
    }

    if (!validateForm()) return;

    // ✅ Нормализуем телефон и сохраняем
    const { isValid, normalized, error } = normalizePhone(formData.phone);
    if (!isValid) {
      setSubmitError(error || 'Некорректный номер телефона');
      return;
    }

    setConfirmedEmail(formData.email.trim().toLowerCase());
    setConfirmedPhone(normalized!);

    setLoading(true);
    try {
      await sendConfirmationCode({ variables: { email: formData.email } });
      setMode('confirm');
      setSubmitError(null);
      setMessage(null);
    } catch (err) {
      setSubmitError('Не удалось отправить код подтверждения');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyConfirmationCode = async () => {
    if (!confirmationCode.trim()) {
      return setSubmitError('Введите код подтверждения');
    }

    if (!confirmedEmail || !confirmedPhone) {
      setSubmitError('Данные регистрации утеряны. Пожалуйста, начните заново.');
      return;
    }

    setLoading(true);
    setSubmitError(null);

    try {
      const { data } = await verifyConfirmationCode({
        variables: {
          email: confirmedEmail,
          code: confirmationCode.trim()
        }
      });

      if (data?.verifyConfirmationCode === true) {
        const { data: signUpData } = await signUp({
          variables: {
            username: formData.username.trim(),
            email: confirmedEmail,
            password: formData.password,
            company: formData.company?.trim() || null,
            phone: confirmedPhone // ✅ Гарантированно нормализованный +7XXXXXXXXXX
          }
        });

        if (signUpData?.signUp?.token) {
          localStorage.setItem('token', signUpData.signUp.token);
          onDismiss();
          window.location.reload();
        } else {
          setSubmitError('Не удалось завершить регистрацию');
        }
      } else {
        setSubmitError('Неверный код подтверждения');
      }
    } catch (err: any) {
      setSubmitError(err.message || 'Ошибка при подтверждении');
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    const { email, password } = formData;
    if (!email.trim() || !password.trim()) {
      return setSubmitError('Введите email и пароль');
    }
    setLoading(true);
    try {
      const { data } = await signIn({
        variables: {
          email: email.trim().toLowerCase(),
          password
        },
        fetchPolicy: "no-cache"
      });

      if (data?.signIn?.token) {
        localStorage.setItem('token', data.signIn.token);
        onDismiss();
        window.location.reload();
      } else {
        setSubmitError('Неверные учётные данные');
      }
    } catch (err: any) {
      setSubmitError(
        err.message.includes("credentials") ||
        err.message.includes("invalid") ||
        err.message.includes("password")
          ? "Неверный email или пароль"
          : "Неверные учётные данные"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!formData.email) {
      setSubmitError('Введите email');
      return;
    }
    try {
      await resetPassword({ variables: { email: formData.email.trim().toLowerCase() } });
      setMessage({ text: 'Новый пароль отправлен на вашу почту', type: MessageBarType.success });
    } catch (err) {
      setSubmitError('Не удалось сбросить пароль');
    }
  };

  const handleSubmit = async () => {
    switch (mode) {
      case 'signup':
        return handleSendConfirmationCode();
      case 'confirm':
        return handleVerifyConfirmationCode();
      case 'signin':
        return handleSignIn();
    }
  };

  const switchMode = () => {
    setMode(mode === 'signup' ? 'signin' : 'signup');
    setSubmitError(null);
    setConfirmationCode('');
    setMessage(null);
    setConfirmedEmail(null);
    setConfirmedPhone(null);
    setPrivacyAccepted(false);
  };

  // ✅ Форматирование телефона при вводе (как в первом фронтенде)
  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length === 0) return '';
    if (digits.length === 1) return `+${digits}`;

    let normalizedDigits = digits;
    if (digits.startsWith('8')) {
      normalizedDigits = '7' + digits.slice(1);
    } else if (!digits.startsWith('7')) {
      normalizedDigits = '7' + digits;
    }

    const part1 = normalizedDigits.slice(1, 4);
    const part2 = normalizedDigits.slice(4, 7);
    const part3 = normalizedDigits.slice(7, 9);
    const part4 = normalizedDigits.slice(9, 11);

    let formatted = `+7`;
    if (part1) formatted += ` (${part1}`;
    if (part2) formatted += `) ${part2}`;
    if (part3) formatted += `-${part3}`;
    if (part4) formatted += `-${part4}`;

    return formatted;
  };

  const handlePhoneChange = (
    e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    const target = e.target as HTMLInputElement;
    if (target.name !== 'phone') return;

    const formatted = formatPhone(newValue || '');
    setFormData(prev => ({ ...prev, phone: formatted }));
    setSubmitError(null);
    setMessage(null);
  };

    // ✅ Обработчик открытия PDF политики конфиденциальности
  const openPrivacyPolicy = () => {
    window.open('/privacy-policy.pdf', '_blank');
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      isBlocking
      styles={{
        main: {
          width: '100%',
          maxWidth: 500,
          padding: 24,
          borderRadius: 8
        }
      }}
    >
      <Stack tokens={{ childrenGap: 16 }}>
        <Text variant="xLarge" block>
          {mode === 'signup'
            ? 'Регистрация'
            : mode === 'confirm'
            ? 'Подтверждение email'
            : 'Вход'}
        </Text>

        {message && <MessageBar messageBarType={message.type}>{message.text}</MessageBar>}
        {submitError && <MessageBar messageBarType={MessageBarType.error}>{submitError}</MessageBar>}

        {mode === 'confirm' ? (
          <TextField
            label="Код подтверждения"
            value={confirmationCode}
            onChange={(_, v) => {
              setConfirmationCode(v || '');
              setSubmitError(null);
              setMessage(null);
            }}
          />
        ) : (
          <>
            {mode === 'signup' && (
              <>
                <TextField
                  label="Имя пользователя"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                />
                <TextField
                  label="Компания (необязательно)"
                  name="company"
                  value={formData.company || ''}
                  onChange={handleChange}
                />
                <TextField
                  label="Телефон"
                  name="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  required
                  placeholder="+7 (999) 999-99-99"
                  description="Для связи при оформлении подписки"
                  errorMessage={mode === 'signup' && submitError?.includes('телефон') ? submitError : undefined}
                />
              </>
            )}
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              errorMessage={emailError ?? undefined}
            />
            <TextField
              label="Пароль"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              canRevealPassword
              revealPasswordAriaLabel="Показать пароль"
              styles={{
                icon: { display: 'none' },
                fieldGroup: { paddingLeft: 10 }
              }}
              required
            />
            {mode === 'signup' && (
              <>
                <TextField
                  label="Повторите пароль"
                  type="password"
                  value={repeatPassword}
                  onChange={(_, val) => {
                    setRepeatPassword(val || '');
                    setSubmitError(null);
                    setMessage(null);
                  }}
                  onPaste={e => e.preventDefault()}
                  required
                />
                <Checkbox
                  checked={privacyAccepted}
                  onChange={(_, checked) => setPrivacyAccepted(!!checked)}
                  onRenderLabel={() => (
                    <span>
                      Соглашаюсь на обработку персональных данных в соответствии с {' '}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          openPrivacyPolicy();
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#0078d4',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          padding: 0,
                          font: 'inherit',
                        }}
                        aria-label="Открыть политику конфиденциальности"
                      >
                        политикой конфиденциальности
                      </button>
                    </span>
                  )}
                />
              </>
            )}
            {mode === 'signin' && (
              <Text
                styles={{
                  root: {
                    cursor: 'pointer',
                    color: '#0078d4',
                    marginTop: -12,
                    textAlign: 'right'
                  }
                }}
                onClick={handleResetPassword}
              >
                Забыли пароль?
              </Text>
            )}
          </>
        )}

        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <PrimaryButton
            text={
              mode === 'signup'
                ? 'Отправить код'
                : mode === 'confirm'
                ? 'Подтвердить'
                : 'Войти'
            }
            onClick={handleSubmit}
            disabled={loading || (mode === 'signup' && !privacyAccepted)}
          />
          <DefaultButton text="Отмена" onClick={onDismiss} disabled={loading} />
        </Stack>

        {mode !== 'confirm' && (
          <Text
            styles={{ root: { cursor: 'pointer', color: '#0078d4' } }}
            onClick={switchMode}
          >
            {mode === 'signup'
              ? 'Уже есть аккаунт? Войти'
              : 'Нет аккаунта? Зарегистрироваться'}
          </Text>
        )}
      </Stack>
    </Modal>
  );
};